import { useState } from "react";
import * as React from "react";

import {
  Button,
  FormHelperText,
  Grid,
  InputLabel,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Divider,
  DialogActions,
  Box,
  Autocomplete,
  Typography,
  useTheme,
  Chip,
  FormControlLabel,
  Switch,
  CircularProgress,
} from "@mui/material";

import * as Yup from "yup";

import { openSnackbar } from "../../../../../api/snackbar";
import { USERAXIOS } from "../../../../../api";
import { SnackbarProps } from "../../../../../types/snackbar";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormik, FormikProvider, Form } from "formik";

import { ResumeTargetJob } from "../../../../../types/resumes-managment/resume";
import { useCountriesForAutocomplete } from "../../../../../hooks/query-hooks/useCountries";
import { useCitiesForAutocomplete } from "../../../../../hooks/query-hooks/useCities";
import { useJobTypesForAutocomplete } from "../../../../../hooks/query-hooks/useJobTypes";
import { useCurrenciesForAutocomplete } from "../../../../../hooks/query-hooks/useCurrencies";
import { useJobAvailabilities } from "../../../../../hooks/query-hooks/useJobAvailabilities";
import { useJobActivities } from "../../../../../hooks/query-hooks/useJobActivities";
import { useDebounce } from "../../../../../hooks";

interface ServerErrors {
  [key: string]: string[];
}

const getInitialValues = (targetJob: ResumeTargetJob | null) => {
  if (targetJob && targetJob.id) {
    return {
      job_availability_id: targetJob.job_availability_id || null,
      salary_expected: targetJob.salary_expected || "",
      currency: targetJob.currency || "",
      confidetial: targetJob.confidetial || false,
      job_types: targetJob.job_types || [],
      job_activities: [],
      desired_work_country_id: null,
      city_ids: [],
    };
  } else {
    return {
      job_availability_id: null,
      salary_expected: "",
      currency: "",
      confidetial: false,
      job_types: [],
      job_activities: [],
      desired_work_country_id: null,
      city_ids: [],
    };
  }
};

const TargetJobSchema = Yup.object().shape({
  job_availability_id: Yup.number().required("Job Availability is required"),
  salary_expected: Yup.string().required("Salary Expected is required"),
  currency: Yup.string().required("Currency is required"),
  confidetial: Yup.boolean(),
  job_types: Yup.array().min(1, "At least one job type must be selected"),
  job_activities: Yup.array().min(
    1,
    "At least one job activity must be selected"
  ),
  desired_work_country_id: Yup.number().required(
    "Desired work country is required"
  ),
  city_ids: Yup.array().min(1, "At least one city must be selected"),
});

function AddEditResumeTargetJobForm({
  setResult,
  resumeId,
  open,
  setOpen,
  item,
  jobSeekerId,
}: {
  setResult: any;
  resumeId: any;
  open: boolean;
  setOpen: (openLanguage: boolean) => void;
  item: ResumeTargetJob | null;
  jobSeekerId: number;
}) {
  const [serverErrors, setServerErrors] = useState<ServerErrors>({});

  // Search states for autocomplete
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [jobTypeSearchTerm, setJobTypeSearchTerm] = useState("");
  const [currencySearchTerm, setCurrencySearchTerm] = useState("");

  // Debounced search terms
  const debouncedCountrySearchTerm = useDebounce(countrySearchTerm, 500);
  const debouncedCitySearchTerm = useDebounce(citySearchTerm, 500);
  const debouncedJobTypeSearchTerm = useDebounce(jobTypeSearchTerm, 500);
  const debouncedCurrencySearchTerm = useDebounce(currencySearchTerm, 500);

  const theme = useTheme();

  const handleClose = () => {
    setOpen(false);
    setServerErrors({});
  };

  const getServerError = (fieldName: string): string | null => {
    if (serverErrors[fieldName] && serverErrors[fieldName].length > 0) {
      return serverErrors[fieldName][0];
    }
    return null;
  };

  const formik = useFormik({
    initialValues: getInitialValues(item ?? null),
    validationSchema: TargetJobSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setServerErrors({});

      try {
        const targetJobData = {
          resumeId: resumeId,
          job_seeker_profile_id: jobSeekerId,
          job_availability_id: values.job_availability_id,
          salary_expected: values.salary_expected.toString(),
          currency: values.currency.toUpperCase(),
          confidetial: values.confidetial,
          job_type_ids: values.job_types.map((jobType: any) => jobType.id),
          job_activity_ids: values.job_activities.map(
            (activity: any) => activity.id
          ),
          desired_work_country_id: values.desired_work_country_id,
          city_ids: values.city_ids.map((city: any) => city.id),
        };

        if (item && item?.id) {
          const response = await USERAXIOS.post(
            `resume/target_jobs/${item?.id}`,
            targetJobData
          );
          const updatedTargetJob = response.data.data;

          if (updatedTargetJob) {
            setResult((prev) =>
              prev.map((item) =>
                item.id === updatedTargetJob.id ? updatedTargetJob : item
              )
            );

            openSnackbar({
              open: true,
              message: "Target Job updated successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        } else {
          const response = await USERAXIOS.post(
            "resume/target_jobs",
            targetJobData
          );
          const newTargetJob = response.data.data;

          if (newTargetJob) {
            setResult((prev) => [...prev, newTargetJob]);

            openSnackbar({
              open: true,
              message: "Target Job Added successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        }

        setSubmitting(false);
        handleClose();
      } catch (error: any) {
        if (error.response?.data?.message) {
          setServerErrors(error.response.data.message);
          console.log("Error ", error.response.data.message);
        } else {
          console.error("Error ", error);
        }

        openSnackbar({
          open: true,
          message: "Something went wrong",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: { color: "error" },
        } as SnackbarProps);
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    values,
    handleChange,
  } = formik;

  const { data: jobAvailabilities = [] } = useJobAvailabilities();

  const { data: jobActivities = [] } = useJobActivities();

  const {
    items: countries,
    loading: loadingCountries,
    hasMore: hasMoreCountries,
    loadMore: loadMoreCountries,
    error: countriesError,
  } = useCountriesForAutocomplete(20, debouncedCountrySearchTerm);

  const {
    items: jobTypes,
    loading: loadingJobTypes,
    hasMore: hasMoreJobTypes,
    loadMore: loadMoreJobTypes,
    error: jobTypesError,
  } = useJobTypesForAutocomplete(20, debouncedJobTypeSearchTerm);

  const {
    items: currencies,
    loading: loadingCurrencies,
    hasMore: hasMoreCurrencies,
    loadMore: loadMoreCurrencies,
    error: currenciesError,
  } = useCurrenciesForAutocomplete(20, debouncedCurrencySearchTerm);

  const {
    items: cities,
    loading: loadingCities,
    hasMore: hasMoreCities,
    loadMore: loadMoreCities,
    error: citiesError,
  } = useCitiesForAutocomplete(
    values?.desired_work_country_id,
    20,
    debouncedCitySearchTerm
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        color="primary"
        style={{
          textAlign: "center",
          fontSize: "22px",
          fontWeight: "bold",
          marginBottom: "10px",
        }}
      >
        {item && item?.id ? (
          <FormattedMessage id="Edit Target Job" />
        ) : (
          <FormattedMessage id="Add Target Job" />
        )}
      </DialogTitle>
      <Divider />

      <DialogContent
        sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2 }}
      >
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Grid container spacing={3} sx={{ flex: 1, overflowY: "auto" }}>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Desired Work Country" />
                  </Typography>
                  <Autocomplete
                    id="desired-work-country-select"
                    options={countries}
                    getOptionLabel={(option: any) =>
                      theme.direction === "rtl"
                        ? option.name_trans.ar
                        : option.name
                    }
                    value={
                      countries.find(
                        (country) =>
                          country.id === values.desired_work_country_id
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      const selectedCountryId = newValue?.id || null;
                      setFieldValue(
                        "desired_work_country_id",
                        selectedCountryId
                      );
                      setFieldValue("city_ids", []);
                      // Clear search term when option is selected
                      if (newValue) {
                        setCountrySearchTerm("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setCountrySearchTerm(newInputValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select desired work country"
                        error={
                          (touched.desired_work_country_id &&
                            Boolean(errors.desired_work_country_id)) ||
                          Boolean(getServerError("desired_work_country_id"))
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCountries && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        const threshold = 100;
                        if (
                          listboxNode.scrollHeight - listboxNode.scrollTop <=
                            listboxNode.clientHeight + threshold &&
                          hasMoreCountries &&
                          !loadingCountries
                        ) {
                          loadMoreCountries();
                        }
                      },
                    }}
                    loading={loadingCountries}
                    filterOptions={(x) => x} // Disable built-in filtering
                    noOptionsText={
                      debouncedCountrySearchTerm.length >= 2
                        ? "No countries found"
                        : "Type to search countries"
                    }
                  />
                  {touched.desired_work_country_id &&
                    errors.desired_work_country_id && (
                      <FormHelperText error>
                        {errors.desired_work_country_id}
                      </FormHelperText>
                    )}
                  {getServerError("desired_work_country_id") && (
                    <FormHelperText error>
                      {getServerError("desired_work_country_id")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Cities" />
                  </Typography>
                  <Autocomplete
                    id="cities-select"
                    multiple
                    options={cities}
                    getOptionLabel={(option: any) =>
                      theme.direction === "rtl"
                        ? option.name_trans.ar
                        : option.name
                    }
                    value={values.city_ids}
                    onChange={(_, newValue) => {
                      setFieldValue("city_ids", newValue);
                      // Clear search term when option is selected
                      if (newValue && newValue.length > 0) {
                        setCitySearchTerm("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setCitySearchTerm(newInputValue);
                      }
                    }}
                    disabled={!values.desired_work_country_id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select cities"
                        error={
                          (touched.city_ids && Boolean(errors.city_ids)) ||
                          Boolean(getServerError("city_ids"))
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCities && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          // key={option.id+option.name}
                          label={
                            theme.direction === "rtl"
                              ? option.name_trans.ar
                              : option.name
                          }
                          {...getTagProps({ index })}
                          size="small"
                        />
                      ))
                    }
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        const threshold = 100;
                        if (
                          listboxNode.scrollHeight - listboxNode.scrollTop <=
                            listboxNode.clientHeight + threshold &&
                          hasMoreCities &&
                          !loadingCities
                        ) {
                          loadMoreCities();
                        }
                      },
                    }}
                    loading={loadingCities}
                    filterOptions={(x) => x} // Disable built-in filtering
                    noOptionsText={
                      debouncedCitySearchTerm.length >= 2
                        ? "No cities found"
                        : "Type to search cities"
                    }
                  />
                  {touched.city_ids && errors.city_ids && (
                    <FormHelperText error>{errors.city_ids}</FormHelperText>
                  )}
                  {getServerError("city_ids") && (
                    <FormHelperText error>
                      {getServerError("city_ids")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Job Availability" />
                  </Typography>
                  <Autocomplete
                    id="job-availability-select"
                    options={jobAvailabilities}
                    getOptionLabel={(option: any) =>
                      theme.direction === "rtl"
                        ? option.name_trans.ar
                        : option.name
                    }
                    value={
                      jobAvailabilities.find(
                        (availability) =>
                          availability.id === values.job_availability_id
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      setFieldValue(
                        "job_availability_id",
                        newValue?.id || null
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select job availability"
                        error={
                          (touched.job_availability_id &&
                            Boolean(errors.job_availability_id)) ||
                          Boolean(getServerError("job_availability_id"))
                        }
                      />
                    )}
                  />
                  {touched.job_availability_id &&
                    errors.job_availability_id && (
                      <FormHelperText error>
                        {errors.job_availability_id}
                      </FormHelperText>
                    )}
                  {getServerError("job_availability_id") && (
                    <FormHelperText error>
                      {getServerError("job_availability_id")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Job Types" />
                  </Typography>
                  <Autocomplete
                    id="job-types-select"
                    multiple
                    options={jobTypes}
                    getOptionLabel={(option: any) =>
                      theme.direction === "rtl"
                        ? option?.name_trans?.ar
                        : option.name
                    }
                    value={values.job_types}
                    onChange={(_, newValue) => {
                      setFieldValue("job_types", newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select job types"
                        error={
                          (touched.job_types && Boolean(errors.job_types)) ||
                          Boolean(getServerError("job_types"))
                        }
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={
                            theme.direction === "rtl"
                              ? option.name_trans?.ar
                              : option.name
                          }
                          {...getTagProps({ index })}
                          size="small"
                        />
                      ))
                    }
                  />
                  {touched.job_types && errors.job_types && (
                    <FormHelperText error>
                      {Array.isArray(errors.job_types)
                        ? errors.job_types.map((error) => error).join(", ")
                        : errors.job_types}
                    </FormHelperText>
                  )}
                  {getServerError("job_types") && (
                    <FormHelperText error>
                      {getServerError("job_types")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Job Activities" />
                  </Typography>
                  <Autocomplete
                    id="job-activities-select"
                    multiple
                    options={jobActivities}
                    getOptionLabel={(option: any) =>
                      theme.direction === "rtl"
                        ? option.name_trans.ar
                        : option.name
                    }
                    value={values.job_activities}
                    onChange={(_, newValue) => {
                      setFieldValue("job_activities", newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select job activities"
                        error={
                          (touched.job_activities &&
                            Boolean(errors.job_activities)) ||
                          Boolean(getServerError("job_activities"))
                        }
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={
                            theme.direction === "rtl"
                              ? option.name_trans.ar
                              : option.name
                          }
                          {...getTagProps({ index })}
                          size="small"
                        />
                      ))
                    }
                  />
                  {touched.job_activities && errors.job_activities && (
                    <FormHelperText error>
                      {errors.job_activities}
                    </FormHelperText>
                  )}
                  {getServerError("job_activities") && (
                    <FormHelperText error>
                      {getServerError("job_activities")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="salary-expected">
                    <FormattedMessage id="Salary Expected" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="salary-expected"
                    name="salary_expected"
                    value={values.salary_expected}
                    onChange={handleChange}
                    placeholder="Salary Expected"
                    type="number"
                    error={
                      (touched.salary_expected &&
                        Boolean(errors.salary_expected)) ||
                      Boolean(getServerError("salary_expected"))
                    }
                  />
                  {touched.salary_expected && errors.salary_expected && (
                    <FormHelperText error>
                      {errors.salary_expected}
                    </FormHelperText>
                  )}
                  {getServerError("salary_expected") && (
                    <FormHelperText error>
                      {getServerError("salary_expected")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Currency" />
                  </Typography>
                  <Autocomplete
                    id="currency-select"
                    options={currencies}
                    getOptionLabel={(option: any) =>
                      theme.direction === "rtl"
                        ? option.name_trans?.ar ?? option.name ?? ""
                        : option.name ?? ""
                    }
                    value={
                      currencies.find(
                        (currency) =>
                          currency.name.toUpperCase() === values.currency
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      setFieldValue("currency", newValue?.name || null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select currency"
                        error={
                          (touched.currency && Boolean(errors.currency)) ||
                          Boolean(getServerError("currency"))
                        }
                      />
                    )}
                  />
                  {touched.currency && errors.currency && (
                    <FormHelperText error>{errors.currency}</FormHelperText>
                  )}
                  {getServerError("currency") && (
                    <FormHelperText error>
                      {getServerError("currency")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                }}
              >
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.confidetial}
                        onChange={(event) => {
                          formik.setFieldValue(
                            "confidetial",
                            event.target.checked
                          );
                        }}
                        id="confidential-switch"
                      />
                    }
                    label="Confidential"
                  />
                  {getServerError("confidetial") && (
                    <FormHelperText error>
                      {getServerError("confidetial")}
                    </FormHelperText>
                  )}
                </Box>
              </Grid>
            </Grid>

            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="center" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                  <Grid item md={2} />
                  <Grid item md={4}>
                    <Button
                      disabled={isSubmitting}
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      <FormattedMessage
                        id={item && item?.id ? "Update" : "Submit"}
                      />
                    </Button>
                  </Grid>
                  <Grid item md={4}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={handleClose}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Grid>
                  <Grid item md={2} />
                </Stack>
              </Grid>
            </DialogActions>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}

export default AddEditResumeTargetJobForm;
